import {
    FcFinePrint as ReportIcon,
    FcFaq as SurveyIcon,
    FcTodoList as PlaybookIcon,
    FcFilledFilter as ComparisonIcon,
} from 'react-icons/fc';
import { ReportingRouterConfig, ReportingRouterInit } from './reportingRouterConfig';
import { ReportingRouter, ReportingRouterEnhancer } from './reportingRouterInterface';
import {
    ReportingContainerBaseConfig,
    ReportingContextBaseConfig,
    ReportingControllerBaseConfig,
    ReportingLoaderBaseConfig,
    ReportingNavigation,
    ReportingRouterInfra,
    ReportingViewBaseConfig,
} from './base';
import {
    createReportChapterView,
    createReportDetailView,
    createReportGenerateContainer,
    createReportGenerateController,
    createReportGenerateLoader,
    createReportGenerateView,
    createReportDetailContainer,
    createReportDetailController,
    createReportDetailLoader,
    createReportNewContainer,
    createReportNewController,
    createReportNewLoader,
    createReportNewView,
    createReportOverviewContainer,
    createReportOverviewController,
    createReportOverviewLoader,
    createReportOverviewView,
    createReportChapterContainer,
    createReportChapterLoader,
    createReportChapterController,
    createContentOverlayContainer,
    createContentOverlayController,
    createContentOverlayLoader,
    createContentOverlayView,
    createContentDetailContainer,
    createContentDetailLoader,
    createDocumentEditorContainer,
    createDocumentEditorLoader,
    createDocumentEditorController,
    createDocumentEditorView,
    createShortcutContainer,
    createShortcutController,
    createShortcutLoader,
    createDocumentNewContainer,
    createDocumentNewLoader,
    createDocumentNewController,
    createDocumentNewView,
    createTaskListView,
    createTaskListLoader,
    createTaskListController,
    createTaskListContainer,
    createTaskItemController,
    createTaskItemLoader,
    createTaskItemContainer,
    createHomeOptionContainer,
    createHomeOptionController,
    createTemplateSelectContainer,
    createTemplateSelectLoader,
    createTemplateSelectController,
    TemplateItemAggregate,
    createDocumentDetailLoader,
    createDocumentDetailContainer,
    createDocumentDetailController,
    createRecordNewContainer,
    createRecordNewLoader,
    createRecordNewController,
    createRecordNewView,
    createRecordFormController,
    createRecordFormView,
    createRecordUpsertContainer,
    createRecordUpsertLoader,
    createRecordUpsertController,
    createRecordUpsertView,
    createRecordEditContainer,
    createRecordEditLoader,
    createRecordEditController,
    createRecordEditView,
    createFieldListContainer,
    createFieldListLoader,
    createFieldListController,
    createFieldListView,
    createSnippetDetailContainer,
    createSnippetDetailLoader,
    createSnippetDetailController,
    createSnippetOverlayContainer,
    createSnippetOverlayView,
    createSnippetOverlayController,
    createSnippetInputContainer,
    createSnippetInputController,
    createSnippetInputEditContainer,
    createSnippetInputEditController,
    createSnippetInputEditLoader,
    createSnippetInputNewContainer,
    createSnippetInputNewController,
    createSnippetInputLabelContainer,
    createSnippetInputLabelLoader,
    createSnippetInputLabelController,
    createRecordFormService,
} from './page';
import {
    createReportSectionItemController,
    createSectionItemContainer,
    createSourceListContainer,
    createSourceListState,
    createSourceListLoader,
    createSourceListController,
    createToolbarItemController,
    createToolbarItemContainer,
    createButtonBlockContainer,
    createButtonBlockController,
    createMentionSpanContainer,
    createMentionSpanController,
    createMentionSpanLoader,
    createResourceBlockController,
    createResourceBlockLoader,
    createResourceBlockContainer,
    createDatabaseBlockContainer,
    createDatabaseBlockController,
    createDatabaseBlockLoader,
    createValueFieldController,
    createValueFieldContainer,
    createCalloutController,
    createCalloutContainer,
    createMentionPreviewController,
    createMentionPreviewLoader,
    createMentionPreviewContainer,
    createTaskBlockController,
    createTaskBlockLoader,
    createTaskBlockContainer,
    createButtonBlockLoader,
    createTextBlockContainer,
    createTextBlockController,
    createTextBlockLoader,
    createHeadingBlockContainer,
    createHeadingBlockLoader,
    createHeadingBlockController,
    createListItemBlockLoader,
    createListItemBlockContainer,
    createListItemBlockController,
    createRelationOneValueContainer,
    createDatabaseStateProvider,
    createDatabaseFilterLoader,
    createDatabaseFilterContainer,
    createDatabaseFilterController,
    createRelationManyValueContainer,
    createRelationOneValueController,
    createRelationOneValueLoader,
    createRelationManyValueController,
    createRelationManyValueLoader,
    createFormBlockController,
    createFormBlockContainer,
    createFormBlockLoader,
    createTextElementContainer,
    createTextElementController,
    createNumberElementController,
    createNumberElementContainer,
    createBooleanElementContainer,
    createBooleanElementController,
    createDatetimeElementContainer,
    createDatetimeElementController,
    ElementComponents,
    createEnumElementController,
    createEnumElementContainer,
    createSnippetElementController,
    createSnippetElementContainer,
    createValueFieldView,
} from './view';
import { createEditor } from '@varos/editor-core';
import { createTiptapEditor } from '@varos/editor-tiptap';
import { AppCustomNode } from '@varos/research-editor';
import { Editor } from '@tiptap/core';
import { createBlockContextProvider } from './view/block/context';
import { IconType } from 'react-icons';
import { keyBy } from '@varos/util-typescript';
import {
    createFormBuilderContainer,
    createFormBuilderView,
    createFormBuilderLoader,
    createFormBuilderController,
    createDocumentFormContainer,
    createDocumentFormLoader,
    createDocumentFormController,
    createDocumentFormView,
    createSharePopoverContainer,
    createSharePopoverController,
    createSharePopoverLoader,
} from './page/document';
import {
    createMemberItemContainer,
    createMemberItemController,
} from './page/common/member/item';
import { createFieldItemContainer, createFieldItemController } from './page/common';
import { TYPE_DEFINITIONS } from './reportingRouterConstant';
import {
    createCollectionController,
    createCollectionItemContainer,
    createCollectionItemController,
    createCollectionItemLoader,
    createCollectionLoader,
} from './page/common/collection';
import {
    createFormCollectionContainer,
    createFormCollectionLoader,
    createFormCollectionController,
    createFormCollectionView,
} from './page/document/form';
import { createValueItemView } from './page/common/value';

export function createReportingRouter(
    config: ReportingRouterInit,
    enhancer?: ReportingRouterEnhancer
) {
    return create({ ...config }, enhancer);
}

function create(
    config: ReportingRouterConfig,
    enhancer?: ReportingRouterEnhancer
): ReportingRouter {
    if (enhancer) {
        return enhancer(create)(config);
    }

    const contextConfig: ReportingContextBaseConfig = {};

    // Create all routers needed for the application
    const reportDetailRouter = config.kernel.provider.createRouter({
        state: {
            kind: 'hash',
        },
    });

    // Create a router map to be used throughout the app
    const router = {
        report: reportDetailRouter,
    };

    const infra: ReportingRouterInfra = {
        editor: createEditor<AppCustomNode, Editor | null>({
            adapter: createTiptapEditor({}),
            debounceDelayMs: 500,
        }),
    };

    const navigation: ReportingNavigation = {
        record: {
            new: {
                open(props) {
                    const [search, setSearch] = props.search;
                    search.set('collection', props.collection);
                    setSearch(search, { replace: true });
                },
            },
        },
        snippet: {
            view: {
                open(props) {
                    const [search, setSearch] = props.search;
                    search.set('snippet', props.snippetId);
                    setSearch(search, { replace: true });
                },
            },
        },
    };

    const containerConfig: ReportingContainerBaseConfig = {
        context: config.context,
        kernel: config.kernel,
        // repository: config.repository,
        UI: config.UI,
        router, // Pass the router map to containers
        repository: config.repository,
        blockContext: createBlockContextProvider(),
        infra,
        state: config.state,
        pubsub: config.pubsub,
        navigation,
    };

    const loaderConfig: ReportingLoaderBaseConfig = {
        repository: config.repository,
    };

    const controllerConfig: ReportingControllerBaseConfig = {
        navigation,
        controller: config.controller,
        kernel: config.kernel,
        infra,
    };

    // Create shared collection components
    const collectionLoader = createCollectionLoader(loaderConfig);
    const collectionController = createCollectionController(controllerConfig);

    const viewConfig: ReportingViewBaseConfig = {
        infra,
        UI: config.UI,
        Layout: config.Layout,
        router,
        kernel: config.kernel,
        Container: {
            Content: {
                Preview: createMentionPreviewContainer(
                    containerConfig,
                    createMentionPreviewLoader(loaderConfig),
                    createMentionPreviewController(controllerConfig)
                ),
            },
            Property: {
                Filter: createDatabaseFilterContainer(
                    containerConfig,
                    createDatabaseFilterLoader(loaderConfig),
                    createDatabaseFilterController(controllerConfig)
                ),
            },
            Block: {
                Text: createTextBlockContainer(
                    containerConfig,
                    createTextBlockLoader(loaderConfig),
                    createTextBlockController(controllerConfig)
                ),
                Heading: createHeadingBlockContainer(
                    containerConfig,
                    createHeadingBlockLoader(loaderConfig),
                    createHeadingBlockController(controllerConfig)
                ),
                ListItem: createListItemBlockContainer(
                    containerConfig,
                    createListItemBlockLoader(loaderConfig),
                    createListItemBlockController(controllerConfig)
                ),
                Button: createButtonBlockContainer(
                    containerConfig,
                    createButtonBlockLoader(loaderConfig),
                    createButtonBlockController(controllerConfig)
                ),
                Resource: createResourceBlockContainer(
                    containerConfig,
                    createResourceBlockLoader(loaderConfig),
                    createResourceBlockController(controllerConfig)
                ),
                Callout: createCalloutContainer(
                    containerConfig,
                    createCalloutController(controllerConfig)
                ),
                DatabaseView: createDatabaseBlockContainer(
                    {
                        ...containerConfig,
                        keys: {
                            recordUrlParam: 'record',
                        },
                    },
                    createDatabaseStateProvider(),
                    createDatabaseBlockLoader({
                        ...loaderConfig,
                        collection: {
                            loader: collectionLoader,
                        },
                    }),
                    createDatabaseBlockController({
                        ...controllerConfig,
                        collection: {
                            controller: collectionController,
                        },
                    })
                ),
                Task: createTaskBlockContainer(
                    containerConfig,
                    createTaskBlockLoader(loaderConfig),
                    createTaskBlockController(controllerConfig)
                ),
                Form: createFormBlockContainer(
                    containerConfig,
                    createFormBlockLoader(loaderConfig),
                    createFormBlockController(controllerConfig)
                ),
            },
            Span: {
                Mention: createMentionSpanContainer(
                    containerConfig,
                    createMentionSpanLoader(loaderConfig),
                    createMentionSpanController(controllerConfig)
                ),
            },
            Toolbar: {
                Item: createToolbarItemContainer(
                    containerConfig,
                    createToolbarItemController(controllerConfig)
                ),
            },
            Shortcut: {
                List: createShortcutContainer(
                    containerConfig,
                    createShortcutLoader(loaderConfig),
                    createShortcutController(controllerConfig)
                ),
            },
        },
    };
    const ReportOverview = createReportOverviewContainer(
        containerConfig,
        createReportOverviewLoader(loaderConfig),
        createReportOverviewController(controllerConfig),
        createReportOverviewView(viewConfig)
    );

    const ReportDetail = createReportDetailContainer(
        containerConfig,
        createReportDetailLoader(loaderConfig),
        createReportDetailController(controllerConfig),
        createReportDetailView({
            ...viewConfig,
            Container: {
                ...viewConfig.Container,
                Source: {
                    List: createSourceListContainer(
                        containerConfig,
                        createSourceListState(),
                        createSourceListLoader(),
                        createSourceListController()
                    ),
                },
            },
        })
    );

    const templates: Array<TemplateItemAggregate & { Icon: IconType }> = [
        {
            id: '1',
            displayName: 'Report',
            caption: 'Discover actionable insights',
            Icon: ReportIcon,
        },
        {
            id: '2',
            displayName: 'Survey',
            caption: 'Learn from people',
            Icon: SurveyIcon,
        },
        {
            id: '3',
            displayName: 'Playbook',
            caption: 'Implement with confidence',
            Icon: PlaybookIcon,
        },
        {
            id: '4',
            displayName: 'Comparison',
            caption: 'Make the right choice',
            Icon: ComparisonIcon,
        },
    ];

    const ReportGenerate = createReportGenerateContainer(
        containerConfig,
        createReportGenerateLoader(loaderConfig),
        createReportGenerateController(controllerConfig),
        createReportGenerateView({
            ...viewConfig,
            Container: {
                ...viewConfig.Container,
                Option: createHomeOptionContainer(
                    containerConfig,
                    createHomeOptionController(controllerConfig)
                ),
                Template: {
                    Select: createTemplateSelectContainer(
                        containerConfig,
                        createTemplateSelectLoader({
                            ...loaderConfig,
                            items: templates,
                        }),
                        createTemplateSelectController({
                            ...controllerConfig,
                            icons: {
                                byId: keyBy(templates, 'id'),
                                default: ReportIcon,
                            },
                        })
                    ),
                },
            },
        })
    );

    const ReportNew = createReportNewContainer(
        containerConfig,
        createReportNewLoader(loaderConfig),
        createReportNewController(controllerConfig),
        createReportNewView(viewConfig)
    );

    const ChapterDetail = createReportChapterContainer(
        containerConfig,
        createReportChapterLoader(loaderConfig),
        createReportChapterController(controllerConfig),
        createReportChapterView({
            ...viewConfig,
            Container: {
                ...viewConfig.Container,
                Section: createSectionItemContainer(
                    containerConfig,
                    createReportSectionItemController(controllerConfig)
                ),
            },
        })
    );

    const ContentOverlay = createContentOverlayContainer(
        containerConfig,
        createContentOverlayLoader(loaderConfig),
        createContentOverlayController(controllerConfig),
        createContentOverlayView(viewConfig)
    );

    const Element: ElementComponents = {
        Text: createTextElementContainer(
            containerConfig,
            createTextElementController(controllerConfig)
        ),
        Number: createNumberElementContainer(
            containerConfig,
            createNumberElementController(controllerConfig)
        ),
        Boolean: createBooleanElementContainer(
            containerConfig,
            createBooleanElementController(controllerConfig)
        ),
        Datetime: createDatetimeElementContainer(
            containerConfig,
            createDatetimeElementController(controllerConfig)
        ),
        Enum: createEnumElementContainer(
            containerConfig,
            createEnumElementController(controllerConfig)
        ),
        Snippet: createSnippetElementContainer(
            containerConfig,
            createSnippetElementController(controllerConfig)
        ),
    };

    const Relation = {
        One: createRelationOneValueContainer(
            containerConfig,
            createRelationOneValueLoader(loaderConfig),
            createRelationOneValueController(controllerConfig)
        ),
        Many: createRelationManyValueContainer(
            containerConfig,
            createRelationManyValueLoader(loaderConfig),
            createRelationManyValueController(controllerConfig)
        ),
    };

    const Value = {
        Field: createValueFieldContainer(
            {
                ...containerConfig,
                Container: {
                    Relation,
                },
            },
            createValueFieldController(controllerConfig),
            createValueFieldView({
                ...viewConfig,
                Container: {
                    Element,
                    Snippet: {
                        Input: createSnippetInputContainer(
                            {
                                ...containerConfig,
                                Container: {
                                    ...viewConfig.Container,
                                    Snippet: {
                                        Edit: createSnippetInputEditContainer(
                                            containerConfig,
                                            createSnippetInputEditLoader(loaderConfig),
                                            createSnippetInputEditController(
                                                controllerConfig
                                            )
                                        ),
                                        New: createSnippetInputNewContainer(
                                            containerConfig,
                                            createSnippetInputNewController(
                                                controllerConfig
                                            )
                                        ),
                                        Label: createSnippetInputLabelContainer(
                                            containerConfig,
                                            createSnippetInputLabelLoader(loaderConfig),
                                            createSnippetInputLabelController(
                                                controllerConfig
                                            )
                                        ),
                                    },
                                },
                            },
                            createSnippetInputController(controllerConfig)
                        ),
                    },
                },
            })
        ),
        Item: createValueFieldContainer(
            {
                ...containerConfig,
                Container: {
                    Relation,
                },
            },
            createValueFieldController(controllerConfig),
            createValueItemView({
                ...viewConfig,
                Container: Element,
            })
        ),
    };

    const Content = {
        Detail: createContentDetailContainer(
            {
                ...containerConfig,
                Container: {
                    Report: ReportDetail,
                    Chapter: ChapterDetail,
                    Document: createDocumentEditorContainer(
                        containerConfig,
                        createDocumentEditorLoader(loaderConfig),
                        createDocumentEditorController(controllerConfig),
                        createDocumentEditorView({
                            ...viewConfig,
                            Container: {
                                ...viewConfig.Container,
                                Value: {
                                    Field: Value.Field,
                                },
                            },
                        })
                    ),
                },
            },
            createContentDetailLoader(loaderConfig)
        ),
    };

    const Record = {
        Upsert: createRecordUpsertContainer(
            {
                ...containerConfig,
                keys: {
                    recordUrlParam: 'record',
                    collectionUrlParam: 'collection',
                },
            },
            createRecordUpsertLoader(loaderConfig),
            createRecordUpsertController(controllerConfig),
            createRecordUpsertView({
                ...viewConfig,
                Container: {
                    New: createRecordNewContainer(
                        {
                            ...containerConfig,
                            service: {
                                record: { form: createRecordFormService() },
                            },
                        },
                        createRecordNewLoader(loaderConfig),
                        createRecordNewController({
                            ...controllerConfig,
                            controller: {
                                record: {
                                    form: createRecordFormController(controllerConfig),
                                },
                            },
                        }),
                        createRecordNewView({
                            ...viewConfig,
                            View: {
                                Form: createRecordFormView({
                                    ...viewConfig,
                                    Container: {
                                        ...viewConfig.Container,
                                        Value: Value.Field,
                                    },
                                }),
                            },
                        })
                    ),
                    Edit: createRecordEditContainer(
                        {
                            ...containerConfig,
                            service: {
                                record: { form: createRecordFormService() },
                            },
                        },
                        createRecordEditLoader(loaderConfig),
                        createRecordEditController({
                            ...controllerConfig,
                            controller: {
                                record: {
                                    form: createRecordFormController(controllerConfig),
                                },
                            },
                        }),
                        createRecordEditView({
                            ...viewConfig,
                            View: {
                                Form: createRecordFormView({
                                    ...viewConfig,
                                    Container: {
                                        ...viewConfig.Container,
                                        Value: Value.Field,
                                    },
                                }),
                            },
                        })
                    ),
                },
            })
        ),
        // Detail: createRecordOverlayContainer(
        //     {
        //         ...containerConfig,
        //         keys: {
        //             recordUrlParam: 'record',
        //         },
        //     },
        //     createRecordOverlayLoader(loaderConfig),
        //     createRecordOverlayController(controllerConfig),
        //     createRecordOverlayView({
        //         ...viewConfig,
        //         Container: {
        //             ...viewConfig.Container,
        //             Value: Value.Field,
        //         },
        //     })
        // ),
    };

    const Snippet = {
        Overlay: createSnippetOverlayContainer(
            {
                ...containerConfig,
                keys: {
                    snippetUrParam: 'snippet',
                },
            },
            createSnippetOverlayController(controllerConfig),
            createSnippetOverlayView({
                ...viewConfig,
                Container: {
                    ...viewConfig.Container,
                    Snippet: {
                        Detail: createSnippetDetailContainer(
                            containerConfig,
                            createSnippetDetailLoader(loaderConfig),
                            createSnippetDetailController(controllerConfig)
                        ),
                    },
                },
            })
        ),
    };

    const TaskList = createTaskListContainer(
        containerConfig,
        createTaskListLoader(loaderConfig),
        createTaskListController(controllerConfig),
        createTaskListView({
            ...viewConfig,
            Container: {
                ...viewConfig.Container,
                Item: createTaskItemContainer(
                    containerConfig,
                    createTaskItemLoader(loaderConfig),
                    createTaskItemController(controllerConfig)
                ),
            },
        })
    );

    const Form = createDocumentFormContainer(
        containerConfig,
        createDocumentFormLoader(loaderConfig),
        createDocumentFormController(controllerConfig),
        createDocumentFormView({
            ...viewConfig,
            Container: {
                ...viewConfig.Container,
                Collection: createFormCollectionContainer(
                    {
                        ...containerConfig,
                        keys: {
                            recordUrlParam: 'record',
                        },
                    },
                    createFormCollectionLoader({
                        ...loaderConfig,
                        collection: {
                            loader: collectionLoader,
                        },
                    }),
                    createFormCollectionController({
                        ...controllerConfig,
                        collection: {
                            controller: collectionController,
                        },
                    }),
                    createFormCollectionView({
                        ...viewConfig,
                        Container: {
                            ...viewConfig.Container,
                            Value: {
                                Item: Value.Item,
                            },
                            Collection: {
                                Item: createCollectionItemContainer(
                                    {
                                        ...containerConfig,
                                        keys: {
                                            recordUrlParam: 'record',
                                        },
                                    },
                                    createCollectionItemLoader(loaderConfig),
                                    createCollectionItemController(controllerConfig)
                                ),
                            },
                        },
                    })
                ),
                Builder: createFormBuilderContainer(
                    containerConfig,
                    createFormBuilderLoader({
                        ...loaderConfig,
                        fieldTypeOptions: TYPE_DEFINITIONS,
                    }),
                    createFormBuilderController(controllerConfig),
                    createFormBuilderView({
                        ...viewConfig,
                        Container: {
                            ...viewConfig.Container,
                            Field: {
                                List: createFieldListContainer(
                                    containerConfig,
                                    createFieldListLoader({
                                        ...loaderConfig,
                                        fieldTypeOptions: TYPE_DEFINITIONS,
                                    }),
                                    createFieldListController(controllerConfig),
                                    createFieldListView({
                                        ...viewConfig,
                                        Container: {
                                            ...viewConfig.Container,
                                            Member: {
                                                Item: createMemberItemContainer(
                                                    createMemberItemController()
                                                ),
                                            },
                                            Field: {
                                                Item: createFieldItemContainer(
                                                    containerConfig,
                                                    createFieldItemController({
                                                        ...controllerConfig,
                                                        typeOptions: TYPE_DEFINITIONS,
                                                    })
                                                ),
                                            },
                                        },
                                    })
                                ),
                            },
                            Share: {
                                Popover: createSharePopoverContainer(
                                    containerConfig,
                                    createSharePopoverLoader(loaderConfig),
                                    createSharePopoverController()
                                ),
                            },
                        },
                    })
                ),
            },
        })
    );

    const Document = {
        New: createDocumentNewContainer(
            containerConfig,
            createDocumentNewLoader(loaderConfig),
            createDocumentNewController(controllerConfig),
            createDocumentNewView(viewConfig)
        ),
        Detail: createDocumentDetailContainer(
            {
                ...containerConfig,
                Container: {
                    Form: Form,
                    Editor: Content.Detail,
                },
            },
            createDocumentDetailLoader(loaderConfig),
            createDocumentDetailController(controllerConfig)
        ),
    };

    return {
        Pages: {
            Report: {
                Overview: ReportOverview,
                Detail: ReportDetail,
                Generate: ReportGenerate,
                New: ReportNew,
            },
            Chapter: {
                Detail: ChapterDetail,
            },
            Content: {
                Overlay: ContentOverlay,
                Detail: Content.Detail,
            },
            Document: {
                New: Document.New,
                Detail: Document.Detail,
            },
            Record: {
                Upsert: Record.Upsert,
            },
            Snippet: {
                Overlay: Snippet.Overlay,
            },
            Task: {
                List: TaskList,
            },
        },
        router,
    };
}
